const md = `# Verifying Cookie Auth in DevTools

Eyvo Demo Agent uses **HttpOnly cookies** for authentication (Sec-S9,
2026-05-26).  The browser holds the session in two cookies set by the
backend on every sign-in — **not** in \`localStorage\` like before.
This page shows you how to verify everything is wired correctly in 60
seconds with Chrome/Edge DevTools.

## Why we moved off localStorage

The JWT used to live in \`localStorage.eda.token\`.  Any cross-site
scripting (XSS) bug — a single \`<script>\` tag injected into a
workflow name, a help page, a share-link description — would have
exfiltrated it.  Cookies marked \`HttpOnly\` are **invisible to
JavaScript**: the browser sends them on every API call automatically,
but \`document.cookie\` can never read them.

## The two cookies you should see

| Cookie         | HttpOnly | Secure | SameSite | Purpose |
|----------------|:--------:|:------:|:--------:|---------|
| \`eda_session\`  | ✅       | ✅     | Lax      | Carries the signed JWT.  Sent on every request, unreadable by JS. |
| \`eda_csrf\`     | ❌       | ✅     | Lax      | A random token the frontend reads with \`document.cookie\` and mirrors into the \`X-CSRF-Token\` header on every POST/PUT/PATCH/DELETE. |

Both cookies expire after **12 hours**.

## Step-by-step verification

1. Sign in to the app (Google → \`@eyvo.com\`).
2. Press **F12** to open DevTools.  Click the **Application** tab
   (Edge calls it **Application** too; Firefox calls it **Storage**).
3. Expand **Storage → Cookies** in the left tree, then click your
   app's origin (e.g. \`https://demo-agent-login.preview.emergentagent.com\`).
4. You should see exactly the two cookies above.  Click \`eda_session\`
   and confirm:
   - **HttpOnly** column: ✓
   - **Secure** column: ✓
   - **SameSite**: \`Lax\`
   - **Expires**: ~12 hours from now (not "Session")
5. Click \`eda_csrf\` — same row layout, but **HttpOnly is unchecked**
   (deliberately — the frontend needs to read it).
6. Now switch to **Storage → Local Storage** on the same origin.
   You should see **only** \`eda.user\` (a JSON blob with your name,
   email, role, and avatar URL).  **You should NOT see**
   \`eda.token\` or \`eda.expires_at\` — those are pre-migration keys
   the new code actively deletes on load.

## Verifying the X-CSRF-Token header on a POST

Any mutating request (POST/PUT/PATCH/DELETE) must carry
\`X-CSRF-Token\`.  The Axios interceptor in \`/app/frontend/src/lib/api.js\`
reads \`eda_csrf\` from \`document.cookie\` and adds the header for you.
To watch it in action:

1. DevTools → **Network** tab.  Filter for \`Fetch/XHR\`.
2. Trigger any mutation in the app — e.g. click **New folder** on
   \`/folders\`, fill the form, hit **Create**.
3. Click the \`/api/folders\` POST in the request list.
4. Open the **Headers** sub-tab and scroll to **Request Headers**.
   You should see:
   \`\`\`
   X-CSRF-Token: <opaque base64-ish string>
   Cookie: eda_session=…; eda_csrf=…
   \`\`\`
   The \`X-CSRF-Token\` value matches the \`eda_csrf\` cookie value.
   Mismatch → backend returns **403 csrf token invalid**.

## What "broken" looks like

| Symptom in DevTools | Likely cause | Fix |
|---------------------|--------------|-----|
| \`eda_session\` cookie missing after login | Login redirect hit a different origin | Check that the URL in the address bar matches \`CORS_ORIGINS\` on the backend |
| \`X-CSRF-Token\` header missing on POST | \`document.cookie\` can't see \`eda_csrf\` (cross-domain block, or extension stripped it) | Disable browser extensions, retry; check ingress isn't dropping \`Set-Cookie\` |
| 401 on every page reload | \`/api/auth/me\` returning 401; cookie expired or backend restarted with a new JWT secret | Sign in again |
| 403 \`csrf token invalid\` on POST | Backend got the cookie but no header, or vice-versa | Hard refresh once (Ctrl+Shift+R) to re-issue both cookies |

## Server-side knobs (admin only)

If you're rotating the JWT secret, all \`eda_session\` cookies become
invalid immediately and every signed-in user is bounced to \`/login\`
on their next API call.  This is by design — see
\`/app/backend/security/auth.py::ensure_jwt_secret\`.
`;

export default md;
